import { useState, FormEvent, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ObfuscatedEmail from '@/components/base/ObfuscatedEmail';

const CONTACT_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'ContactPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/contact`,
      url: `${import.meta.env.VITE_SITE_URL}/contact`,
      name: 'Contact Vixtra | Inquiries & Partnerships',
      description:
        'Reach out to Vixtra for inquiries about our innovative products and services, partnership opportunities, or general questions. We respond in English and French.',
      inLanguage: 'en',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: `${import.meta.env.VITE_SITE_URL}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contact',
            item: `${import.meta.env.VITE_SITE_URL}/contact`,
          },
        ],
      },
    },
    {
      '@type': 'Organization',
      '@id': `${import.meta.env.VITE_SITE_URL}/#organization`,
      name: 'Vixtra',
      url: import.meta.env.VITE_SITE_URL,
      telephone: '+16138000431',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+16138000431',
        contactType: 'customer service',
        availableLanguage: ['English', 'French'],
      },
    },
  ],
};

const ContactPage = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const id = 'schema-contact';
    let el = document.getElementById(id) as HTMLScriptElement | null;
    if (!el) {
      el = document.createElement('script');
      el.id = id;
      el.type = 'application/ld+json';
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(CONTACT_SCHEMA);
    document.title = 'Contact Vixtra | Inquiries & Partnerships';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/contact`);
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute('content') ?? '';
    if (metaDesc) metaDesc.setAttribute('content', 'Reach out to Vixtra for inquiries about our innovative products and services, partnership opportunities, or general questions. We respond in English and French.');
    const metaKw = document.querySelector('meta[name="keywords"]');
    const prevKw = metaKw?.getAttribute('content') ?? '';
    if (metaKw) metaKw.setAttribute('content', 'Vixtra contact, innovative products inquiries, partnership, combination products');
    return () => {
      const s = document.getElementById(id);
      if (s) s.remove();
      document.title = 'Vixtra | Innovative Products & Services';
      if (canonical) canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/`);
      if (metaDesc) metaDesc.setAttribute('content', prevDesc);
      if (metaKw) metaKw.setAttribute('content', prevKw);
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement).value;
    if (message.length > 500) return;
    setSubmitting(true);
    const data = new URLSearchParams();
    const formData = new FormData(form);
    formData.forEach((value, key) => { data.append(key, value as string); });
    try {
      await fetch('https://readdy.ai/api/form/d78iqpjll6jbq2vddb6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch { /* silent */ } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <section className="relative pt-32 pb-20 px-6 md:px-10 bg-stone-900 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/30bddf717d55ff48d4a69ecc4ca346d2.jpg"
            alt="Vixtra office"
            className="w-full h-full object-cover object-top opacity-30"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <span className="text-green-400 text-xs font-semibold uppercase tracking-[0.2em] mb-4 block">{t('contact_page_tag')}</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4">{t('contact_page_title')}</h1>
          <p className="text-white/60 text-base max-w-xl leading-relaxed mb-6">{t('contact_page_subtitle')}</p>
          <nav className="flex items-center gap-2 text-sm text-white/40">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">{t('nav_home')}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white/70">{t('contact_breadcrumb')}</span>
          </nav>
        </div>
      </section>

      <main className="flex-1 py-20 px-6 md:px-10 bg-stone-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div>
              <span className="text-green-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3 block">{t('contact_info_tag')}</span>
              <h2 className="text-2xl md:text-3xl font-bold text-stone-900 mb-3">{t('contact_info_title')}</h2>
              <p className="text-stone-500 text-sm leading-relaxed">{t('contact_info_desc')}</p>
            </div>
            <div className="flex items-start gap-4 bg-white border border-stone-100 rounded-xl p-5 hover:border-green-200 transition-colors duration-200">
              <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                <i className="ri-mail-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="text-stone-500 text-xs mb-1">{t('contact_email_label')}</p>
                <ObfuscatedEmail className="text-stone-900 font-semibold text-sm hover:text-green-600 transition-colors" />
              </div>
            </div>
            <a href="tel:+16138000431" className="flex items-start gap-4 bg-white border border-stone-100 rounded-xl p-5 hover:border-green-200 transition-colors duration-200 cursor-pointer">
              <div className="w-11 h-11 flex items-center justify-center bg-green-50 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-green-600 text-lg"></i>
              </div>
              <div>
                <p className="text-stone-500 text-xs mb-1">{t('contact_phone_label')}</p>
                <p className="text-stone-900 font-semibold text-sm">+1 (613) 800-0431</p>
              </div>
            </a>
          </div>

          <div className="lg:col-span-3 bg-white border border-stone-100 rounded-2xl p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-50 rounded-full mb-5">
                  <i className="ri-check-line text-green-600 text-3xl"></i>
                </div>
                <h3 className="text-stone-900 font-bold text-xl mb-2">{t('contact_success_title')}</h3>
                <p className="text-stone-500 text-sm max-w-xs leading-relaxed">{t('contact_success_desc')}</p>
              </div>
            ) : (
              <>
                <h3 className="text-stone-900 font-bold text-xl mb-6">{t('contact_form_title')}</h3>
                <form data-readdy-form id="vixtra-contact-form" onSubmit={handleSubmit} className="flex flex-col gap-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_first_name')} <span className="text-red-400">*</span></label>
                      <input type="text" name="first_name" required placeholder="John" className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-green-500 transition-colors" />
                    </div>
                    <div>
                      <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_last_name')} <span className="text-red-400">*</span></label>
                      <input type="text" name="last_name" required placeholder="Doe" className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-green-500 transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_email')} <span className="text-red-400">*</span></label>
                    <input type="email" name="email" required placeholder="john@example.com" className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-green-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_phone')}</label>
                    <input type="tel" name="phone" placeholder="+1 (613) 000-0000" className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-green-500 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_subject')}</label>
                    <select name="subject" className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 focus:outline-none focus:border-green-500 transition-colors cursor-pointer">
                      <option value="">{t('contact_subject_placeholder')}</option>
                      <option value="Products & Services">{t('contact_subject_products')}</option>
                      <option value="Partnership Inquiry">{t('contact_subject_partnership')}</option>
                      <option value="General Inquiry">{t('contact_subject_general')}</option>
                      <option value="Other">{t('contact_subject_other')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-stone-700 text-xs font-semibold mb-1.5 uppercase tracking-wide">{t('contact_message')} <span className="text-red-400">*</span></label>
                    <textarea name="message" required rows={5} maxLength={500} placeholder={t('contact_message_placeholder')} onChange={(e) => setCharCount(e.target.value.length)} className="w-full border border-stone-200 rounded-md px-4 py-2.5 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:border-green-500 transition-colors resize-none" />
                    <p className={`text-xs mt-1 text-right ${charCount > 500 ? 'text-red-500' : 'text-stone-400'}`}>{charCount}/500</p>
                  </div>
                  <button type="submit" disabled={submitting || charCount > 500} className="whitespace-nowrap cursor-pointer w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors duration-200 text-sm">
                    {submitting ? t('contact_submitting') : t('contact_submit')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
